<template>
  <div class="post-to-deal-test">
    <div class="container">
      <h1>Post to Deal Processing</h1>
      <p class="subtitle">Run one pipeline call and inspect final output</p>

      <div class="card">
        <h2>Test Input</h2>
        <label class="label">Post Text</label>
        <textarea
          v-model="postText"
          rows="6"
          class="form-control"
          placeholder="Paste the social media post text here..."
        />
        <div class="button-row">
          <button class="btn-primary" :disabled="isProcessing || !postText.trim()" @click="processPost">
            {{ isProcessing ? 'Processing...' : 'Process Post to Deal' }}
          </button>
          <button class="btn-secondary" :disabled="isProcessing" @click="resetResults">
            Reset
          </button>
        </div>
      </div>

      <div class="card" v-if="scrapedPostData">
        <h2>Final Deal Information</h2>
        <div class="summary-grid">
          <div class="summary-item">
            <strong>Product ID</strong>
            <span>{{ finalDeal?.productId || scrapedPostData?.productId || 'N/A' }}</span>
            <div class="evidence-ids">
              <span class="evidence-label">Evidence ids</span>
              {{ formatEvidenceIdList(finalDealFieldEvidenceIds.productId) }}
            </div>
          </div>
          <div class="summary-item">
            <strong>Retail Price</strong>
            <span>{{ formatCurrency(finalDeal?.retailPrice) }}</span>
            <div class="evidence-ids">
              <span class="evidence-label">Evidence ids</span>
              {{ formatEvidenceIdList(finalDealFieldEvidenceIds.retailPrice) }}
            </div>
          </div>
          <div class="summary-item">
            <strong>Sale Price</strong>
            <span>{{ formatCurrency(resolvedSalePrice) }}</span>
            <div class="evidence-ids">
              <span class="evidence-label">Evidence ids</span>
              {{ formatEvidenceIdList(finalDealFieldEvidenceIds.salePrice) }}
            </div>
          </div>
          <div class="summary-item">
            <strong>Final Price</strong>
            <span>{{ formatCurrency(finalDeal?.finalPrice ?? scrapedPostData?.productData?.finalPrice) }}</span>
            <div class="evidence-ids">
              <span class="evidence-label">Evidence ids</span>
              {{ formatEvidenceIdList(finalDealFieldEvidenceIds.finalPrice) }}
            </div>
          </div>
          <div class="summary-item">
            <strong>Discount Percent</strong>
            <span>{{ resolvedDiscountPercentDisplay }}</span>
            <div class="evidence-ids">
              <span class="evidence-label">Evidence ids</span>
              {{ formatEvidenceIdList(finalDealFieldEvidenceIds.discountPercent) }}
            </div>
          </div>
          <div class="summary-item">
            <strong>Promo Code</strong>
            <span>{{ finalDeal?.promoCode || scrapedPostData?.promoCode || 'N/A' }}</span>
            <div class="evidence-ids">
              <span class="evidence-label">Evidence ids</span>
              {{ formatEvidenceIdList(finalDealFieldEvidenceIds.promoCode) }}
            </div>
          </div>
          <div class="summary-item">
            <strong>Coupon</strong>
            <span>{{ finalDeal?.couponInfo?.couponCode || (finalDeal?.couponInfo?.hasCoupon ? 'Has coupon' : 'N/A') }}</span>
            <div class="evidence-ids">
              <span class="evidence-label">Evidence ids</span>
              {{ formatEvidenceIdList(finalDealFieldEvidenceIds.coupon) }}
            </div>
          </div>
          <div class="summary-item">
            <strong>Coupon Type</strong>
            <span>{{ finalDeal?.couponInfo?.couponType || scrapedPostData?.couponInfo?.couponType || 'N/A' }}</span>
            <div class="evidence-ids">
              <span class="evidence-label">Evidence ids</span>
              {{ formatEvidenceIdList(finalDealFieldEvidenceIds.coupon) }}
            </div>
          </div>
          <div class="summary-item full-width horizontal-item">
            <strong>Deal Type</strong>
            <div class="horizontal-value">
              <span class="deal-type-badge" :class="`deal-type-${getDealTypeClass(resolvedDealType)}`">
                {{ getDealTypeName(resolvedDealType) }}
              </span>
              <span class="horizontal-evidence">{{ formatEvidenceIdList(dealTypeEvidenceIds) }}</span>
            </div>
          </div>
          <div class="summary-item full-width horizontal-item">
            <strong>Link</strong>
            <div class="horizontal-value">
              <a v-if="finalDeal?.link" :href="finalDeal.link" target="_blank">{{ finalDeal.link }}</a>
              <span v-else>N/A</span>
            </div>
          </div>
        </div>
      </div>

      <div class="card error-card" v-if="errorMessage">
        <h3>Error</h3>
        <p>{{ errorMessage }}</p>
      </div>

      <div class="card debug-card" v-if="rawResponse">
        <h3>Debug Info (Raw Backend Response)</h3>
        <details>
          <summary>Show Raw JSON</summary>
          <pre class="debug-json">{{ rawResponseJson }}</pre>
        </details>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import api from '@/api'
import ScrapedPostData, { FinalDealObservation } from '@/classes/ScrapedPostData'
import { formatJsonPretty } from '@/utils/jsonFormat'
import { buildFinalDealFieldEvidenceIds, formatEvidenceIdList } from '@/domain/finalDealEvidenceSources'

const postText = ref<string>('')
const isProcessing = ref(false)
const errorMessage = ref<string | null>(null)
const scrapedPostData = ref<ScrapedPostData | null>(null)
const rawResponse = ref<any | null>(null)

const finalDeal = computed<FinalDealObservation | null>(() => {
  return scrapedPostData.value?.evidence?.finalDeal ?? null
})

const resolvedDealType = computed<number | undefined>(() => {
  if (finalDeal.value?.dealType !== null && finalDeal.value?.dealType !== undefined) {
    return Number(finalDeal.value.dealType)
  }
  if (scrapedPostData.value?.dealType !== null && scrapedPostData.value?.dealType !== undefined) {
    return Number(scrapedPostData.value.dealType)
  }
  return undefined
})

const rawResponseJson = computed(() => formatJsonPretty(rawResponse.value))
const finalDealFieldEvidenceIds = computed(() =>
  buildFinalDealFieldEvidenceIds(scrapedPostData.value?.evidence)
)
const dealTypeEvidenceIds = computed(() => {
  const ids = [
    ...finalDealFieldEvidenceIds.value.discountPercent,
    ...finalDealFieldEvidenceIds.value.promoCode,
    ...finalDealFieldEvidenceIds.value.coupon,
    ...finalDealFieldEvidenceIds.value.finalPrice
  ]
  return [...new Set(ids)].sort()
})
const resolvedSalePrice = computed<number | null | undefined>(() => {
  return scrapedPostData.value?.discountInfo?.salePrice
    ?? scrapedPostData.value?.productData?.salePrice
})
const resolvedDiscountPercentDisplay = computed(() => {
  const discountInfo = scrapedPostData.value?.discountInfo
  if (discountInfo?.type === 'Percent' && discountInfo.value !== null && discountInfo.value !== undefined) {
    return `${Number(discountInfo.value).toFixed(0)}%`
  }

  const retail = finalDeal.value?.retailPrice
  const final = finalDeal.value?.finalPrice ?? scrapedPostData.value?.productData?.finalPrice
  if (retail && final && retail > 0 && retail > final) {
    const percent = ((retail - final) / retail) * 100
    return `${percent.toFixed(0)}%`
  }

  return 'N/A'
})

const processPost = async () => {
  if (!postText.value.trim()) {
    alert('Please enter post text')
    return
  }

  isProcessing.value = true
  errorMessage.value = null
  rawResponse.value = null
  scrapedPostData.value = null

  try {
    const response = await api.processPostToDeal(postText.value)
    rawResponse.value = response.data
    scrapedPostData.value = new ScrapedPostData(response.data?.scrapedPostData || {})
  } catch (error: any) {
    errorMessage.value = error?.message || 'An error occurred while processing the post'
  } finally {
    isProcessing.value = false
  }
}

const resetResults = () => {
  postText.value = ''
  isProcessing.value = false
  errorMessage.value = null
  rawResponse.value = null
  scrapedPostData.value = null
}

const formatCurrency = (value?: number | null): string => {
  if (value === null || value === undefined) {
    return 'N/A'
  }
  return `$${value}`
}

const getDealTypeName = (dealType?: number): string => {
  const dealTypeNames: Record<number, string> = {
    0: 'Unknown',
    1: 'Sale Price Deal',
    2: 'Coupon Deal',
    3: 'Promo Code Deal',
    4: 'Sale Price + Promo Code',
    5: 'Sale Price + Coupon',
    6: 'Sale Price + Coupon + Promo Code',
    7: 'Coupon + Promo Code Deal',
    8: 'Good Price Deal'
  }
  return dealTypeNames[Number(dealType ?? 0)] || 'Unknown'
}

const getDealTypeClass = (dealType?: number): string => {
  const dealTypeClasses: Record<number, string> = {
    0: 'unknown',
    1: 'sale-price-deal',
    2: 'coupon-deal',
    3: 'promo-code-deal',
    4: 'sale-price-with-promo-code',
    5: 'sale-price-with-coupon',
    6: 'sale-price-with-coupon-and-promo-code',
    7: 'coupon-and-promo-code-deal',
    8: 'good-price-deal'
  }
  return dealTypeClasses[Number(dealType ?? 0)] || 'unknown'
}
</script>

<style scoped>
.post-to-deal-test {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px 20px;
}

.container {
  max-width: 1100px;
  margin: 0 auto;
}

h1 {
  color: white;
  text-align: center;
  margin-bottom: 8px;
}

.subtitle {
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  margin-bottom: 24px;
}

.card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.label {
  display: block;
  margin-bottom: 8px;
  color: #555;
  font-weight: 600;
}

.form-control {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: block;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  padding: 10px 12px;
  resize: vertical;
}

.button-row {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.btn-primary,
.btn-secondary {
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  cursor: pointer;
  color: white;
  font-weight: 600;
}

.btn-primary {
  background: #4f46e5;
}

.btn-secondary {
  background: #6b7280;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.summary-item {
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px;
}

.summary-item strong {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.full-width {
  grid-column: 1 / -1;
}

.horizontal-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.horizontal-item strong {
  margin-bottom: 0;
  min-width: 120px;
}

.horizontal-value {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.horizontal-value a {
  overflow-wrap: anywhere;
}

.horizontal-evidence {
  font-size: 12px;
  color: #64748b;
}

.evidence-ids {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed #e2e8f0;
  font-size: 12px;
  color: #475569;
  line-height: 1.45;
  word-break: break-word;
}

.evidence-label {
  display: block;
  font-weight: 600;
  color: #334155;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.deal-type-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
}

.deal-type-badge.deal-type-unknown {
  background: #f1f5f9;
  color: #475569;
}

.deal-type-badge.deal-type-sale-price-deal {
  background: #e0f2fe;
  color: #075985;
}

.deal-type-badge.deal-type-coupon-deal {
  background: #fff7ed;
  color: #9a3412;
}

.deal-type-badge.deal-type-promo-code-deal {
  background: #f5f3ff;
  color: #5b21b6;
}

.deal-type-badge.deal-type-sale-price-with-promo-code {
  background: #dcfce7;
  color: #166534;
}

.deal-type-badge.deal-type-sale-price-with-coupon {
  background: #fef3c7;
  color: #92400e;
}

.deal-type-badge.deal-type-sale-price-with-coupon-and-promo-code {
  background: #fce7f3;
  color: #9d174d;
}

.deal-type-badge.deal-type-coupon-and-promo-code-deal {
  background: #ede9fe;
  color: #6d28d9;
}

.deal-type-badge.deal-type-good-price-deal {
  background: #ecfccb;
  color: #3f6212;
}

.error-card {
  border-left: 4px solid #dc2626;
}

.debug-json {
  margin: 0;
  background: #f6f8fa;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  padding: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 480px;
  overflow: auto;
  text-align: left;
}

a {
  color: #4f46e5;
}

.debug-card {
  text-align: left;
}

.debug-card summary {
  cursor: pointer;
  font-weight: 600;
  margin-bottom: 10px;
}
</style>
